// The menu bundle (ops plan 2026-10-14-staff-agents, doc 18 "Menu build playbook"; lane O with D1's review): QA
// builds a new store's menu in their own local Radish Next, the merchant approves that exact version in the review link
// (`onboarding.menu.recordApproval`), and `catalog.applyMenuBundle` writes it to the location in one transaction,
// through the catalog's own commands. A bundle is the DESIRED state of the location's v1 menu, with the draft's own ids.
// Core's `planMenuBundle` fingerprints and diffs it (one place, for Desk, the preview and the apply).
//
// Out of v1 (coordinator, 2026-10-15): product images, tax profiles, discount rules, printers and dining options. A
// bundle names a tax profile, tax exemption, age restriction or printer by id only, and the location must have it.
import { z } from 'zod';
import { defineError } from '../../define/errors.ts';
import { defineEvent } from '../../define/event.ts';
import { defineCommand, defineQuery } from '../../define/operations.ts';
import {
  CategoryId, CourseId, DepartmentId, DiscountRuleId, IsoInstant, LocationId, ModifierGroupId, ProductId, Rate, TaxExemptionId, TaxProfileId, VariantId,
} from '../../primitives/codecs.ts';
import { CloudAuthoritative } from '../config/index.ts';
import { CatalogEntityNotFound, CatalogInvalid } from './errors.ts';
import { CategoryFields, DiscountRuleFields, ProductFields, TaxProfileFields } from './index.ts';
import { CourseFields, DepartmentFields, TaxExemptionFields } from './menu.ts';
import { ProductImageType } from './images.ts';
import { ModifierGroupFields, VariantFields } from './modifiers.ts';

/** The most catalog commands one apply records (core `MENU_BUNDLE_MAX_STEPS`; D1 and X, 2026-10-15). */
export const MENU_BUNDLE_MAX_STEPS = 1500;


/** A product as a bundle carries it: on sale (`archived` and `available` are the apply's and the store's to set). */
export const MenuBundleProductFields = ProductFields.omit({ archived: true, available: true }).meta({ id: 'MenuBundleProductFields' });

/**
 * A product's image in a bundle: a sha256 staged for this account (onboarding.menu.stageImage). Absent leaves the
 * store's image as it is; null removes it, and the diff lists the removal.
 */
export const MenuBundleImage = z.object({ sha256: z.string().regex(/^[0-9a-f]{64}$/), contentType: ProductImageType }).meta({ id: 'MenuBundleImage' });

export const MenuBundle = z
  .object({
    /** 2 adds product images; a version 1 bundle reads as one with no image changes. */
    schemaVersion: z.union([z.literal(1), z.literal(2)]),
    locationId: LocationId,
    departments: z.array(z.object({ departmentId: DepartmentId, shared: z.boolean(), fields: DepartmentFields })).max(500),
    courses: z.array(z.object({ courseId: CourseId, shared: z.boolean(), fields: CourseFields })).max(100),
    categories: z.array(z.object({ categoryId: CategoryId, shared: z.boolean(), fields: CategoryFields })).max(500),
    modifierGroups: z.array(z.object({ modifierGroupId: ModifierGroupId, shared: z.boolean(), fields: ModifierGroupFields })).max(500),
    products: z.array(z.object({ productId: ProductId, shared: z.boolean(), fields: MenuBundleProductFields, image: MenuBundleImage.nullable().optional() })).max(MENU_BUNDLE_MAX_STEPS),
    variants: z.array(z.object({ variantId: VariantId, shared: z.boolean(), fields: VariantFields })).max(MENU_BUNDLE_MAX_STEPS),
    // ---- v2's money rows (coordinator, 2026-10-16): each is matched to the store's row of the same name at publish
    // (a tax profile by its exact rate, an exemption by its dining options, a discount by every field) and created only
    // when the store has none; a bundle never changes or removes one the store has.
    /** Tax profiles the draft's items use. A new one is never the location's default. */
    taxProfiles: z.array(z.object({ taxProfileId: TaxProfileId, fields: TaxProfileFields.omit({ isLocationDefault: true }) })).max(50).optional(),
    /** Tax exempt overrides (their dining options are matched to the store's by name). */
    taxExemptions: z.array(z.object({ taxExemptionId: TaxExemptionId, fields: TaxExemptionFields })).max(100).optional(),
    /** Discounts, comps and price adjustments, their targets naming the bundle's items and categories. */
    discountRules: z.array(z.object({ discountRuleId: DiscountRuleId, shared: z.boolean(), fields: DiscountRuleFields })).max(500).optional(),
  })
  .meta({ id: 'MenuBundle' });

/** `sha256:` and 64 hex digits (core `menuBundleFingerprint`, `planMenuBundle`'s `diffDigest`). */
export const MenuDigest = z.string().regex(/^sha256:[0-9a-f]{64}$/).meta({ id: 'MenuDigest' });

/** What a bundle can remove from a store: never a tax profile, exemption or discount (MenuApproved v2's removals). */
export const MenuRemovalKind = z.enum(['department', 'course', 'category', 'modifierGroup', 'product', 'variant', 'productImage']).meta({ id: 'MenuRemovalKind' });

export const MenuEntityKind = z
  .enum(['department', 'course', 'category', 'modifierGroup', 'taxProfile', 'taxExemption', 'product', 'variant', 'productImage', 'discountRule'])
  .meta({ id: 'MenuEntityKind' });

const MenuChange = z.object({
  kind: MenuEntityKind,
  id: z.uuid(),
  name: z.string(),
  /** Wire values: a price is `{ minor, currency }`. `before` is null on a create. */
  changes: z.array(z.object({ field: z.string(), before: z.unknown(), after: z.unknown() })),
});

/**
 * Something at the location the bundle leaves out: a product sold there, or any product once the location has orders,
 * is archived; anything else is deleted. Removals are listed apart so staff and the merchant see each one.
 */
export const MenuRemoval = z
  .object({ kind: MenuRemovalKind, id: z.uuid(), name: z.string(), action: z.enum(['archive', 'delete']), sold: z.boolean() })
  .meta({ id: 'MenuRemoval' });

export const MenuBundleDiff = z
  .object({ creates: z.array(MenuChange), updates: z.array(MenuChange), removals: z.array(MenuRemoval) })
  .meta({ id: 'MenuBundleDiff' });

/**
 * A row a draft names but a v1 bundle never carries, as the staff member's local stack has it: publishPreview maps each
 * to the store's production row of the same name (a tax profile: and the exact same rate).
 */
export const MenuReference = z
  .object({ kind: z.enum(['taxProfile', 'taxExemption', 'ageRestriction', 'printer', 'diningOption']), id: z.uuid(), name: z.string().max(255), rate: Rate.optional() })
  .meta({ id: 'MenuReference' });

/** A production tax profile the bundle names, so the review shows each item's tax by name. */
export const MenuTaxProfile = z.object({ taxProfileId: z.uuid(), name: z.string(), rate: Rate }).meta({ id: 'MenuTaxProfile' });

// ---- errors ----------------------------------------------------------------------------------------------------------

/** One location's menu review: the same id across every republish of its draft. */
export const MenuPreviewId = z.uuid({ version: 'v7' }).meta({ id: 'MenuPreviewId' });


/** The merchant approved a version that is no longer the newest: the page reloads the newest for them. */
export const OnboardingPreviewStale = defineError({
  code: 'onboarding.preview_stale', httpStatus: 409, message: 'errors.onboarding.preview_stale', retryable: false,
  details: z.object({ previewId: MenuPreviewId, latestRevision: z.int().positive() }),
});

/** No preview has this id at this location. */
export const OnboardingPreviewNotFound = defineError({
  code: 'onboarding.preview_not_found', httpStatus: 404, message: 'errors.onboarding.preview_not_found', retryable: false,
  details: z.object({ previewId: MenuPreviewId }),
});

/** The draft names a tax profile, tax exemption, age restriction or printer the store's production rows don't match. */
export const OnboardingReferencesUnmatched = defineError({
  code: 'onboarding.references_unmatched', httpStatus: 422, message: 'errors.onboarding.references_unmatched', retryable: false,
  details: z.object({
    problems: z.array(z.object({
      problem: z.enum(['unmatched_reference', 'ambiguous_reference', 'rate_mismatch', 'differs_from_store']),
      kind: z.enum(['taxProfile', 'taxExemption', 'ageRestriction', 'printer', 'diningOption', 'discountRule']),
      localId: z.uuid(),
      name: z.string(),
      localRate: z.string().optional(),
      productionRates: z.array(z.string()).optional(),
    })),
  }),
});

/** No merchant approval names this bundle's fingerprint, or the location's preview moved on since the approval. */
export const MenuBundleNotApproved = defineError({
  code: 'catalog.bundle_not_approved', httpStatus: 409, message: 'errors.catalog.bundle_not_approved', retryable: false,
  details: z.object({ locationId: LocationId, fingerprint: MenuDigest }),
});

/** The apply would remove something the approved preview did not list, or archive-to-delete one it listed. */
export const MenuBundleRemovalsNotApproved = defineError({
  code: 'catalog.bundle_removals_not_approved', httpStatus: 409, message: 'errors.catalog.bundle_removals_not_approved', retryable: false,
  details: z.object({ ids: z.array(z.uuid()) }),
});

/** The location changed since the dry run the staff member approved: dry-run again and approve the new diff. */
export const MenuBundleDiffChanged = defineError({
  code: 'catalog.bundle_diff_changed', httpStatus: 409, message: 'errors.catalog.bundle_diff_changed', retryable: false,
  details: z.object({ expected: MenuDigest, actual: MenuDigest }),
});

/** The merchant's approval was already used: an approval applies once, so a later run never overwrites edits made since. */
export const MenuBundleAlreadyApplied = defineError({
  code: 'catalog.bundle_already_applied', httpStatus: 409, message: 'errors.catalog.bundle_already_applied', retryable: false,
  details: z.object({ approvalId: z.uuid() }),
});

/** More catalog commands than one transaction may record. */
export const MenuBundleTooLarge = defineError({
  code: 'catalog.bundle_too_large', httpStatus: 422, message: 'errors.catalog.bundle_too_large', retryable: false,
  details: z.object({ steps: z.int(), max: z.int() }),
});

// ---- the command -----------------------------------------------------------------------------------------------------

export const MenuBundleApplied = defineEvent({
  name: 'MenuBundleApplied',
  version: 1,
  summary: "A merchant-approved menu bundle was written to the location's catalog in one transaction.",
  aggregate: 'config',
  payload: z.object({
    locationId: LocationId,
    fingerprint: MenuDigest,
    approvalId: z.uuid(),
    created: z.int().nonnegative(),
    updated: z.int().nonnegative(),
    archived: z.int().nonnegative(),
    deleted: z.int().nonnegative(),
    at: IsoInstant,
  }),
  verifiable: ['locationId', 'fingerprint', 'approvalId'],
  merge: 'lifecycle',
  deviceConsumed: false,
});

export const ApplyMenuBundle = defineCommand({
  name: 'catalog.applyMenuBundle',
  summary:
    "Write a merchant-approved menu to a location (the stored bundle of its newest preview revision): the dry run shows every create, update and removal; the real run writes them all through the catalog's own commands in one transaction, only for the approved fingerprint, and only if the diff is the one the staff member approved.",
  input: z.object({
    locationId: LocationId,
    /** The preview revision the merchant approved: its stored bundle is what is applied, never a copy sent again. */
    previewId: MenuPreviewId,
    revision: z.int().positive(),
    /**
     * Required on a real run: the `diffDigest` of the dry run the staff member approved in Desk. A location that changed
     * since gives another diff, and the run is refused.
     */
    expectedDiffDigest: MenuDigest.optional(),
  }),
  output: z.object({
    fingerprint: MenuDigest,
    diff: MenuBundleDiff,
    diffDigest: MenuDigest,
    /** A merchant approval names this fingerprint, its preview was not republished since, and it was not applied yet. */
    approved: z.boolean(),
    applied: z.boolean(),
  }),
  permission: 'onboarding.menu.manage',
  // Every step is also authorized as its own catalog command (items.manage, modifiers.manage) against the same principal.
  backofficeAlso: ['items.manage'],
  risk: 'high',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason:
    "Applies only a menu the merchant approved in the review link, which the cloud holds. A staged account (legacy still its system of record) is refused: only the importer writes one.",
  emits: [MenuBundleApplied],
  errors: [CatalogInvalid, CatalogEntityNotFound, CloudAuthoritative, OnboardingPreviewNotFound, OnboardingPreviewStale, MenuBundleNotApproved, MenuBundleRemovalsNotApproved, MenuBundleDiffChanged, MenuBundleAlreadyApplied, MenuBundleTooLarge],
  dryRun: true,
  agent: {
    expose: true,
    surfaces: ['staff.onboarding'],
    description:
      "Always dry-run first and show the diff; the real run needs the staff approval and the dry run's diffDigest. A large menu takes up to about a minute: wait at least 60 s, and read an idempotency conflict on a retry of the same command id as still running, never as a failure.",
  },
});

export const ExportMenuBundle = defineQuery({
  name: 'onboarding.menu.exportBundle',
  summary:
    "The location's menu as a bundle (no archived products), with the names and rates of the tax profiles, exemptions, age restrictions and printers it names: Launchpad reads the staff member's local draft with it before publishing the preview.",
  input: z.object({ locationId: LocationId }),
  output: z.object({ bundle: MenuBundle, references: z.array(MenuReference) }),
  permission: 'onboarding.menu.manage',
  availability: 'location',
  scope: 'location',
  // A stored row the bundle cannot carry is named (rule menu_bundle.export_unreadable and the field), never a bare 500.
  errors: [CatalogInvalid],
  agent: { expose: true, surfaces: ['staff.onboarding'], description: 'The location menu as it is now, in one read: start here before changing it.' },
});

export const menuBundleCommands = [ApplyMenuBundle] as const;
export const menuBundleQueries = [ExportMenuBundle] as const;
export const menuBundleEvents = [MenuBundleApplied] as const;
